import React, { useCallback, useState } from 'react';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { DateFormatters } from 'app/infrastructure/data/types';
import { SecondaryTextMode, TranslationsComponent } from 'app/components/translation';
import { SpinLoader } from 'app/components';

import { AdministrationMessagesTranslation, maxLengthMessage, maxLengthSubject } from '../../constants';
import { AdministrationMessagesFormState, AdministrationMessageItem, MessagesValidation } from '../types-store';

import { MessageFormContainer } from './message-form-container';
import { MessageFormActive } from './message-form-active';

interface MessageFormRendererProps {
  formState: AdministrationMessagesFormState;
  mainLanguage: number;
  canEdit: boolean;
  canDelete: boolean;
  shouldPrompt: boolean;

  deleteMessage: (id: number, successMessage: string) => void;
  saveMessage: (message: AdministrationMessageItem,
    successMessage: string,
    validationErrorMessage: string) => void;

  onSubjectChange: (languageId: number, subject: string) => void;
  onMessageChange: (languageId: number, message: string) => void;
  copySubjectToAllTranslations: (languageId: number, successMessage: string) => void;
  copyMessageToAllTranslations: (languageId: number, successMessage: string) => void;
  changeActive: (active: boolean) => void;
  updateValidation: (validation: MessagesValidation) => void;
  displayError: (errorMessage: string) => void;

  translation: AdministrationMessagesTranslation;
  formatters: DateFormatters;
}

export const MessageFormRenderer: React.FC<MessageFormRendererProps> = ({
  formState, mainLanguage, canEdit, canDelete, shouldPrompt, 
  deleteMessage, saveMessage,
  onSubjectChange, onMessageChange,
  copySubjectToAllTranslations, copyMessageToAllTranslations,
  changeActive,
  updateValidation, displayError,
  translation, formatters
}) => {

  const { message, fetching, languageOptions, validation } = formState;

  const [showTranslations, switchTranslationsToggle] = useState(true);

  const copySubjectToAll = useCallback(
    () => {
      !!mainLanguage && copySubjectToAllTranslations(mainLanguage, translation?.subject_copied_to_all);
    },
    [mainLanguage, translation]
  );

  const copyMessageToAll = useCallback(
    () => {
      !!mainLanguage && copyMessageToAllTranslations(mainLanguage, translation?.message_copied_to_all);
    },
    [mainLanguage, translation]
  );

  if (!languageOptions || fetching) {
    return <SpinLoader />;
  }

  return <MessageFormContainer
    formState={formState}
    mainLanguage={mainLanguage}
    canEdit={canEdit}
    canDelete={canDelete}
    shouldPrompt={shouldPrompt}
    switchTranslationsToggle={switchTranslationsToggle}
    deleteMessage={deleteMessage}
    saveMessage={saveMessage}
    updateValidation={updateValidation}
    displayError={displayError}
    translation={translation}
    formatters={formatters} >
    <MainGridContainer>
      <MessageFormActive
        message={message}
        readOnly={!canEdit}
        onChange={changeActive}
        translation={translation}
      />
    </MainGridContainer>
    <TranslationsComponent
      primaryText={message?.translations?.map(t => ({ value: t.language, description: t.subject }))}
      secondaryText={message?.translations?.map(t => ({ value: t.language, description: t.message }))}
      primaryTextValueKey="subject"
      secondaryTextValueKey="message"
      primaryTextMaxLength={maxLengthSubject}
      secondaryTextMaxLength={maxLengthMessage}
      languageOptions={languageOptions}
      mainLanguage={mainLanguage}
      secondaryTextMode={SecondaryTextMode.required}
      readOnly={!canEdit}
      onPrimaryTextChange={onSubjectChange}
      onSecondaryTextChange={onMessageChange}
      copyPrimaryTextToAllTranslations={copySubjectToAll}
      copySecondaryTextToAllTranslations={copyMessageToAll}
      hasTranslationsToggled={showTranslations}
      switchTranslationsToggle={switchTranslationsToggle}
      validation={validation?.translations}
      validationLongDescription={validation?.longDescription}
      primaryTextLabel={translation?.subject}
      secondaryTextLabel={translation?.message}
      copyPrimaryTextToAllLabel={translation?.copy_subject_to_all}
      copySecondaryTextToAllLabel={translation?.copy_message_to_all}
      translationsHeaderLabel={translation?.translations_header}
      switchTranslationsToggleLabel={translation?.show_translations}
    />
  </MessageFormContainer>;
};
